import dayjs from 'dayjs';
import { cn } from '@shared/utils';
import type { GetLettersContentResponse, GetLettersResponse } from '@shared/api';
import AttentionIcon from '@assets/images/attention-icon.svg';
import ArchiveIcon from '@assets/images/trash-icon.svg';
import ArchiveMobileIcon from '@assets/images/archive-colored-icon_mobile.svg';
import TrashIcon from '@assets/images/archive-colored-icon.svg';
import TrashMobileIcon from '@assets/images/trash-icon_mobile.svg';
import AttachIcon from '@assets/images/attach.svg';

interface LetterProps {
  className?: string;
  idx: number;
  letter: GetLettersResponse;
  onReadContent: (letter: GetLettersResponse) => void;
  onImportant: (item: GetLettersResponse) => void;
  onArchive: (item: GetLettersResponse) => void;
  onDelete: (item: GetLettersResponse | GetLettersContentResponse) => void;
}

export const Letter = (props: LetterProps) => {
  return (
    <tr
      className={cn(
        'group my-px h-16.5 cursor-pointer',
        'max-md:relative max-md:mb-4 max-md:block max-md:h-auto max-md:rounded-lg max-md:bg-white max-md:p-4',
        props.idx % 2 === 0 ? ['bg-[#efefef]', 'max-md:bg-white'] : ['bg-[#e6e6e6]', 'max-md:bg-white'],
        props.className,
      )}
      onClick={() => props.onReadContent(props.letter)}
    >
      <td
        className={cn(
          'h-17 max-h-17 min-h-17 items-center border-y border-l border-transparent text-center font-medium text-[#4a4a4a]',
          'group-hover:border-y group-hover:border-l group-hover:border-[#e5c79d]',
          'max-md:hidden',
        )}
      >
        <div className={cn('cursor-pointer', 'last:ml-4', 'hover:underline')} onClick={(e) => (e.stopPropagation(), props.onImportant(props.letter))}>
          {props.letter.important && <AttentionIcon />}
        </div>
      </td>
      <td
        className={cn(
          'border-y border-transparent text-center font-medium text-[#4a4a4a]',
          'group-hover:border-y group-hover:border-[#e5c79d]',
          'max-md:mb-7 max-md:block max-md:h-auto max-md:bg-white max-md:text-left max-md:text-[#0f0f11]',
        )}
      >
        {dayjs(props.letter.date).format('DD MMM YYYY')}
      </td>
      <td
        className={cn(
          'border-y border-transparent text-center font-medium text-[#4a4a4a]',
          'group-hover:border-y group-hover:border-[#e5c79d]',
          'max-md:mb-1 max-md:block max-md:h-auto max-md:w-full max-md:bg-white max-md:text-left max-md:text-[#0f0f11]',
        )}
        valign='middle'
      >
        <div className={cn('flex items-center justify-center gap-2', 'max-md:justify-between')}>
          <span className={cn('hidden text-[#4c4c4c80]', 'max-md:inherit')}>Subject</span>
          {!!props.letter.attachments?.length && <AttachIcon />}
          <span className={cn('overflow-hidden text-ellipsis', 'max-md:pl-4 max-md:whitespace-nowrap')}>{props.letter.subject}</span>
        </div>
      </td>
      <td
        className={cn(
          'border-y border-transparent text-center font-medium text-[#4a4a4a]',
          'group-hover:border-y group-hover:border-[#e5c79d]',
          'max-md:flex max-md:h-auto max-md:w-full max-md:items-center max-md:justify-between max-md:bg-white max-md:text-left max-md:text-[#0f0f11]',
        )}
      >
        <span className={cn('hidden text-[#4c4c4c80]', 'max-md:inherit')}>Time</span>
        <span className={cn('overflow-hidden text-ellipsis', 'max-md:pl-4 max-md:whitespace-nowrap')}>
          {dayjs(props.letter.date).format('h:mm a')}
        </span>
      </td>
      <td
        className={cn(
          'border-y border-r border-transparent text-center font-medium text-[#4a4a4a]',
          'group-hover:border-y group-hover:border-r group-hover:border-[#e5c79d]',
          'max-md:block max-md:h-auto max-md:bg-white max-md:text-left max-md:text-[#0f0f11]',
        )}
      >
        <div className={cn('flex px-8', 'max-md:p-0 max-md:pt-4')}>
          {!props.letter.archive && (
            <div
              className={cn(
                'mx-3 flex cursor-pointer flex-col items-center justify-center pt-3',
                'hover:underline',
                'max-md:m-0 max-md:h-9 max-md:w-full max-md:flex-row max-md:gap-2 max-md:rounded-l-lg max-md:bg-[#fafafa] max-md:p-0',
              )}
              onClick={(e) => (e.stopPropagation(), props.onArchive(props.letter))}
            >
              <ArchiveIcon className='max-md:hidden' />
              <ArchiveMobileIcon className={cn('hidden', 'max-md:inherit')} />
              <p className={cn('my-2 text-center text-[9px] font-medium text-[#4a4a4a]', 'max-md:text-base max-md:text-[#4c4c4c50]')}>Archive</p>
            </div>
          )}
          <div
            className={cn(
              'mx-3 flex cursor-pointer flex-col items-center justify-center pt-3',
              'hover:underline',
              'max-md:relative max-md:m-0 max-md:h-9 max-md:w-full max-md:flex-row max-md:gap-2 max-md:rounded-r-lg max-md:bg-[#fafafa] max-md:p-0',
              'max-md:before:absolute max-md:before:top-1.25 max-md:before:bottom-1.25 max-md:before:left-0 max-md:before:w-px max-md:before:bg-[#4c4c4c10]',
            )}
            onClick={(e) => (e.stopPropagation(), props.onDelete(props.letter))}
          >
            <TrashIcon className='max-md:hidden' />
            <TrashMobileIcon className={cn('hidden', 'max-md:inherit')} />
            <p className={cn('my-2 text-center text-[9px] font-medium text-[#4a4a4a]', 'max-md:text-base max-md:text-[#4c4c4c50]')}>Delete</p>
          </div>
        </div>
      </td>
    </tr>
  );
};
